import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from 'mobx';
import { Path } from 'react-hook-form';
import ErrorContextToFieldConverter from '@alfa-retail-react/components/converter/ErrorContextToFieldConverter';

import { LoadStateStore } from 'app/loading/LoadStateStore';
import { OrderService } from 'app/service/OrderService';
import { CardOrder } from 'app/model/CardOrder';
import { OrderFormStore } from 'app/logic/pages/order/OrderFormStore';
import { ReferenceService } from 'app/service/ReferenceService';
import { Reference } from 'app/model/Reference';
import { OrderFormValues } from 'app/logic/pages/order/OrderPage';
import { MapBuilder } from 'app/utils/MapBuilder';
import { LocalityTypeStore } from 'app/logic/address/LocalityTypeStore';
import { CardOrderCancelReason } from 'app/model/CardOrderCancelReason';
import { ProductService } from 'app/service/ProductService';

export class OrderPageStore {
    private readonly BELARUS_COUNTRY_CODE = '112';

    readonly loadStateStore = new LoadStateStore();

    readonly actionStateStore = new LoadStateStore(true);

    private readonly orderService = new OrderService();

    private readonly referenceService = new ReferenceService();

    private readonly localityTypesStore = new LocalityTypeStore();

    private readonly productService = new ProductService();

    orderFormStore: OrderFormStore;

    @observable
    private _order: CardOrder;

    @observable
    private _documentType: Reference;

    @observable
    conditionAgreement: boolean = false;

    @observable
    residencyCountryReference: Reference;

    @observable
    citizenshipCountryReference: Reference;

    constructor(
        /**
         * Персонализируем заявку единожды после получения токена МСИ.
         * Во всех остальных случаях при попадании на страницу персонализация заявки не требуется.
         */
        private readonly shouldPersonalizeOrder: boolean,
    ) {
        makeObservable(this);
    }

    /**
     * Маппинг контекстов ошибок валидации к полям формы.
     * Преобразует путь к полю модели в путь к полю формы.
     *
     * В случае, когда путь к полю формы === null - ошибка отображается к странице.
     * @private
     */
    readonly contextConverter =
        new ErrorContextToFieldConverter<OrderFormValues>(
            MapBuilder.create<Path<CardOrder>, Array<Path<OrderFormValues>>>()
                .value('client.surnameLatin', ['surnameLatin'])
                .value('client.nameLatin', ['nameLatin'])
                .value('client.email', ['email'])
                .value('client.isUnderFatca', ['fatca'])
                .value('client.secretWord', ['secretWord'])

                .value('client.birthPlace', [])
                .value('client.birthPlace.countryCode', ['birthCountryCode'])
                .value('client.birthPlace.soatoCode', ['belarusLocality'])
                .value('client.birthPlace.region', ['belarusLocality'])
                .value('client.birthPlace.district', ['belarusLocality'])
                .value('client.birthPlace.villageCouncil', ['belarusLocality'])
                .value('client.birthPlace.localityType', [
                    'birthLocalityType',
                    'belarusLocality',
                ])
                .value('client.birthPlace.locality', [
                    'birthLocality',
                    'belarusLocality',
                ])

                .value('client.identity', [])

                .value('client.registrationAddress', [])
                .value('client.registrationAddress.soatoCode', [
                    'registrationAddress.locality',
                ])
                .value('client.registrationAddress.region', [
                    'registrationAddress.locality',
                ])
                .value('client.registrationAddress.district', [
                    'registrationAddress.locality',
                ])
                .value('client.registrationAddress.villageCouncil', [
                    'registrationAddress.locality',
                ])
                .value('client.registrationAddress.localityType', [
                    'registrationAddress.locality',
                ])
                .value('client.registrationAddress.locality', [
                    'registrationAddress.locality',
                ])
                .value('client.registrationAddress.streetType', [
                    'registrationAddress.street',
                ])
                .value('client.registrationAddress.street', [
                    'registrationAddress.street',
                ])
                .value('client.registrationAddress.house', [
                    'registrationAddress.house',
                ])
                .value('client.registrationAddress.building', [
                    'registrationAddress.building',
                ])
                .value('client.registrationAddress.flat', [
                    'registrationAddress.flat',
                ])
                .value('client.registrationAddress.postalCode', [
                    'registrationAddress.postalCode',
                ])

                .value('client.residentialAddress', [])
                .value('client.residentialAddress.soatoCode', [
                    'residentialAddress.locality',
                ])
                .value('client.residentialAddress.region', [
                    'residentialAddress.locality',
                ])
                .value('client.residentialAddress.district', [
                    'residentialAddress.locality',
                ])
                .value('client.residentialAddress.villageCouncil', [
                    'residentialAddress.locality',
                ])
                .value('client.residentialAddress.localityType', [
                    'residentialAddress.locality',
                ])
                .value('client.residentialAddress.locality', [
                    'residentialAddress.locality',
                ])
                .value('client.residentialAddress.streetType', [
                    'residentialAddress.street',
                ])
                .value('client.residentialAddress.street', [
                    'residentialAddress.street',
                ])
                .value('client.residentialAddress.house', [
                    'residentialAddress.house',
                ])
                .value('client.residentialAddress.building', [
                    'residentialAddress.building',
                ])
                .value('client.residentialAddress.flat', [
                    'residentialAddress.flat',
                ])
                .value('client.residentialAddress.postalCode', [
                    'residentialAddress.postalCode',
                ])
                .value('client.isMarketingConsentSigned', [
                    'marketingCommunication',
                ])
                .value('client.marketingConsentList', ['marketingConsent'])
                .defaultValue([])
                .build(),
        );

    @action
    async initialize(orderId: string) {
        return this.loadStateStore.loadData(async () => {
            const rawOrder = await this.orderService.getById(orderId);

            await this.productService.checkRestrictions(
                rawOrder.cardProductCode,
                orderId,
            );

            const personalizedRaw =
                await this.orderService.personalize(rawOrder);

            /**
             * https://projects.alfabank.by/browse/XSEC2C-1965
             *
             * Поля адреса должны быть заполнены из МСИ если они есть. Если их нету, поля заполняются из черновика.
             *
             * Поле e-mail заполняется из черновика по-умолчанию,
             * т.к. это поле более вероятно будет заполнено актуальным адресом в процессе заполнения черновика,
             * чем подтянутое из МСИ
             */
            const personalized = this.shouldPersonalizeOrder
                ? {
                      ...personalizedRaw,
                      client: {
                          ...personalizedRaw.client,
                          email:
                              rawOrder.client.email ||
                              personalizedRaw.client.email,
                          registrationAddress:
                              personalizedRaw.client.registrationAddress ||
                              rawOrder.client.registrationAddress,
                          residentialAddress:
                              personalizedRaw.client.residentialAddress ||
                              rawOrder.client.residentialAddress,
                          birthPlace:
                              personalizedRaw.client.birthPlace ||
                              rawOrder.client.birthPlace,
                      },
                  }
                : personalizedRaw;

            const latinCredentials = {
                surnameLatin:
                    rawOrder.client.surnameLatin ||
                    personalized.client.surnameLatin ||
                    '',
                nameLatin:
                    rawOrder.client.nameLatin ||
                    personalized.client.nameLatin ||
                    '',
            };

            const order = this.shouldPersonalizeOrder ? personalized : rawOrder;

            const [
                documentType,
                localityTypesReference,
                streetTypesReference,
                country,
                residencyCountryReference,
                citizenshipCountryReference,
            ] = await Promise.all([
                this.referenceService.getDocumentTypeById(
                    order.client.identity.type,
                ),
                this.referenceService.getLocalityTypesReference(),
                this.referenceService.getStreetTypesReference(),
                this.referenceService.getCountryById(this.BELARUS_COUNTRY_CODE),
                this.referenceService.getCountryById(
                    order.client.residencyCountryCode,
                ),
                this.referenceService.getCountryById(
                    order.client.citizenshipCountryCode,
                ),
                this.localityTypesStore.initialize(),
            ]);

            this.orderFormStore = new OrderFormStore({
                latinCredentials,
                localityTypesReference,
                streetTypesReference,
                country,
                cardOrder: order,
                personalizedOrder: personalized,
                localityTypesStore: this.localityTypesStore,
            });

            runInAction(() => {
                this.documentType = documentType;
                this.order = order;
                this.residencyCountryReference = residencyCountryReference;
                this.citizenshipCountryReference = citizenshipCountryReference;
            });

            await this.orderFormStore.initialize();
        });
    }

    @computed
    get order() {
        return this._order;
    }

    @computed
    get documentType() {
        return this._documentType;
    }

    set order(order: CardOrder) {
        this._order = order;
    }

    set documentType(documentType: Reference) {
        this._documentType = documentType;
    }

    cancelOrder = (
        orderId: string,
        reason: CardOrderCancelReason,
    ): Promise<void> => {
        return this.actionStateStore.loadData(() => {
            return this.orderService.cancelOrder(orderId, reason);
        });
    };
}
